import { MantineProvider, createTheme, type MantineColorsTuple } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

// i18n 초기화 (import 시점에 i18next.init 실행 — 리소스 번들 동기 로드)
import "@/lib/i18n";
import { toDayjsLocale, useLang } from "@/lib/i18n/useLang";

/**
 * 'brand' 인디고 10단계 — HTML 시안 디자인 토큰.
 * 중심(인덱스 6) #4f46e5, hover(7) #4338ca, 밝은 활성배경(0) #eef2ff.
 * Mantine 은 filled 버튼에 인덱스 6, hover 에 7 을 씀.
 */
const brand: MantineColorsTuple = [
  "#eef2ff", // 0 — 밝은 활성 배경 (사이드바 active, light variant bg)
  "#e0e7ff", // 1
  "#c7d2fe", // 2
  "#a5b4fc", // 3
  "#818cf8", // 4
  "#6366f1", // 5
  "#4f46e5", // 6 — 브랜드 중심 (primary filled)
  "#4338ca", // 7 — hover
  "#3730a3", // 8
  "#312e81", // 9
];

/**
 * eQMS 브랜드 테마 - Mantine 디자인 시스템 단일화 (HTML 시안 토큰)
 * - primaryColor: 'brand'(인디고)
 * - defaultRadius: 'md'(~8px)
 * - 폰트: Pretendard 유지
 */
const eqmsTheme = createTheme({
  primaryColor: "brand",
  colors: { brand },
  fontFamily:
    '"Pretendard Variable", "Pretendard", -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", sans-serif',
  defaultRadius: "md",
});

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: 1,
      },
    },
  });
}

interface ProvidersProps {
  children: ReactNode;
}

/**
 * 앱 전역 Provider 스택
 * - MantineProvider: UI 테마 + 한국어
 * - QueryClientProvider: TanStack Query 서버 상태
 */
export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(makeQueryClient);
  const { lang } = useLang();

  // 세션 hydration(isHydrated)은 App 부트스트랩의 getUser() 완료 후에만 표시한다(App.tsx).
  // 여기서 조기 setHydrated 를 호출하면 getUser 완료 전에 AuthGuard 가 미인증으로 오판해
  // 새로고침 시 /login 으로 튕긴다(레이스). 따라서 Providers 는 hydration 을 다루지 않음.

  return (
    <MantineProvider theme={eqmsTheme} defaultColorScheme="light">
      <DatesProvider settings={{ locale: toDayjsLocale(lang) }}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
